/* eslint-disable max-lines-per-function, complexity, max-lines */
/* Cohesive single-file section component — see project guidelines. */
import { useState } from "react";
import { motion } from "framer-motion";
import { PHASES } from "@/data/site";
import { ChevronRight, Check } from "lucide-react";

export const Accelerator = () => {
    const [active, setActive] = useState(0);

    return (
        <section
            id="programs"
            data-testid="accelerator-section"
            className="relative py-12 md:py-16 bg-[#0A0A0A] overflow-hidden"
        >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-x-10 gap-y-6">
                    {/* Row 1 LEFT — title + paragraph */}
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 02 — The Accelerator
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            9 Weeks. One Goal.
                            <br />
                            <span className="gold-text-gradient">
                                Takeover.
                            </span>
                        </h2>
                        <p className="mt-6 max-w-md text-white/65 leading-relaxed">
                            Every cohort runs the same proven 5-phase blueprint
                            — from brand architecture to a live grand finale
                            showcase. Real deliverables. Real performances.
                            Real receipts.
                        </p>
                    </div>

                    {/* Row 1 RIGHT — borderless program-tier logos sitting parallel to the title */}
                    <div className="lg:col-span-7 self-center">
                        <div className="grid grid-cols-3 gap-3 md:gap-4">
                            {[
                                {
                                    tier: "Mini Mogul",
                                    age: "Elementary Age",
                                    logo: "/assets/mini-mogul-logo.png",
                                },
                                {
                                    tier: "Junior Mogul",
                                    age: "Junior High Age",
                                    logo: "/assets/junior-mogul-logo.png",
                                },
                                {
                                    tier: "Young Mogul",
                                    age: "High School Age",
                                    logo: "/assets/young-mogul-logo.png",
                                },
                            ].map((p) => (
                                <div
                                    key={p.tier}
                                    data-testid={`program-tier-${p.tier.toLowerCase().replace(/\s/g, "-")}`}
                                    className="group flex flex-col items-center text-center"
                                >
                                    <div className="h-28 w-28 md:h-32 md:w-32 flex items-center justify-center">
                                        <img
                                            src={p.logo}
                                            alt={`${p.tier} Program`}
                                            className="max-h-full max-w-full object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.35)] group-hover:drop-shadow-[0_0_35px_rgba(212,175,55,0.6)] group-hover:scale-105 transition-all duration-500"
                                        />
                                    </div>
                                    <div className="mt-3 font-display text-base md:text-lg uppercase tracking-tight text-white leading-tight">
                                        {p.tier}
                                    </div>
                                    <div className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1 font-bold">
                                        {p.age}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Timeline left */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            {/* Vertical line */}
                            <div className="absolute left-6 top-0 bottom-0 w-px bg-[#2A2A2A]" />
                            <motion.div
                                key={active}
                                initial={{ height: 0 }}
                                animate={{
                                    height: `${(active + 1) * (100 / PHASES.length)}%`,
                                }}
                                transition={{ duration: 0.7, ease: "easeOut" }}
                                className="absolute left-6 top-0 w-px bg-gradient-to-b from-[#FFDF73] via-[#D4AF37] to-[#D4AF37]/0"
                                style={{ boxShadow: "0 0 12px #D4AF37" }}
                            />

                            {PHASES.map((p, i) => {
                                const isActive = i === active;
                                const done = i < active;
                                return (
                                    <button
                                        key={p.title}
                                        data-testid={`phase-node-${i}`}
                                        onClick={() => setActive(i)}
                                        className="group relative w-full text-left pl-16 pr-4 py-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                                    >
                                        <div
                                            className={(() => {
                                                // Phase-node indicator state: active > done > pending
                                                let state;
                                                if (isActive)
                                                    state =
                                                        "border-[#D4AF37] bg-[#D4AF37] gold-glow-strong";
                                                else if (done)
                                                    state =
                                                        "border-[#D4AF37]/60 bg-[#D4AF37]/30";
                                                else
                                                    state =
                                                        "border-[#2A2A2A] bg-[#050505]";
                                                return `absolute left-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full grid place-items-center border-2 transition-all ${state}`;
                                            })()}
                                        >
                                            {done && !isActive && (
                                                <Check size={12} className="text-black" />
                                            )}
                                            {isActive && (
                                                <span className="h-2 w-2 rounded-full bg-black" />
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div
                                                className={`font-accent text-xl ${
                                                    isActive
                                                        ? "text-[#D4AF37]"
                                                        : "text-white/50"
                                                }`}
                                            >
                                                {p.week}
                                            </div>
                                            <div
                                                className={`font-display uppercase tracking-tight text-lg leading-snug mt-1 ${
                                                    isActive
                                                        ? "text-white"
                                                        : "text-white/60 group-hover:text-white/85"
                                                }`}
                                            >
                                                {p.title}
                                            </div>
                                        </div>
                                        <ChevronRight
                                            size={18}
                                            className={`transition-transform ${
                                                isActive
                                                    ? "text-[#D4AF37] translate-x-1"
                                                    : "text-white/30"
                                            }`}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Phase detail right */}
                    <div className="lg:col-span-7">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="glass-gold p-8 md:p-12 relative h-full min-h-[480px]"
                            data-testid="phase-detail"
                        >
                            <div className="absolute top-6 right-6 font-display text-7xl md:text-9xl text-[#D4AF37]/10 leading-none">
                                0{active + 1}
                            </div>
                            <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                                Phase {active + 1} of {PHASES.length}
                            </span>
                            <h3 className="mt-3 font-display text-3xl md:text-5xl uppercase tracking-tight leading-[1] max-w-lg whitespace-pre-line">
                                {PHASES[active].title}
                            </h3>
                            <p className="mt-6 text-white/75 leading-relaxed text-base md:text-lg max-w-xl">
                                {PHASES[active].desc}
                            </p>

                            <div className="mt-10">
                                <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-4">
                                    Deliverables
                                </div>
                                <ul className="grid sm:grid-cols-2 gap-3">
                                    {PHASES[active].deliverables.map((d) => (
                                        <li
                                            key={d}
                                            className="flex items-start gap-3 text-white/85"
                                        >
                                            <span className="mt-1.5 h-2 w-2 bg-[#D4AF37] rotate-45 shrink-0" />
                                            <span className="text-sm">{d}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between">
                                <button
                                    data-testid="phase-prev"
                                    onClick={() =>
                                        setActive(
                                            (active - 1 + PHASES.length) %
                                                PHASES.length,
                                        )
                                    }
                                    className="text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-[#D4AF37]"
                                >
                                    ← Previous Phase
                                </button>
                                <button
                                    data-testid="phase-next"
                                    onClick={() =>
                                        setActive((active + 1) % PHASES.length)
                                    }
                                    className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] hover:text-[#FFF3A1]"
                                >
                                    Next Phase →
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
